import { useMemo, useState } from 'react';

import { EventDate, useShowEvent, useShowEventDate } from '@givebacks/api/store';
import { useNavigate } from 'react-router-dom';
import { Alert, Divider, Modal, Stack, Text } from 'ui';
import { IconTicket } from 'ui/Icons';

import { EventDateTimeCard } from '../../../molecules/EventDateTimeCard/EventDateTimeCard';
import { TicketsSection } from '../../../molecules/TicketsSection/TicketsSection';

interface EventDateModalContentProps {
  causeId?: string;
  eventId?: string;
  eventDateId?: string;
  eventDate?: EventDate;
}

const EventDateModalContent = ({
  causeId,
  eventId,
  eventDateId,
  eventDate,
}: EventDateModalContentProps) => {
  const eventDateRemainingCapacity = useMemo(() => {
    if (!eventDate?.inventory?.remaining_capacity) return null;
    return eventDate.inventory.remaining_capacity;
  }, [eventDate?.inventory?.remaining_capacity]);

  const hasTickets = useMemo(() => {
    if (!eventDate?.event_date_tickets) return false;
    return eventDate?.event_date_tickets?.length > 0;
  }, [eventDate?.event_date_tickets]);

  /**
   * Show the capacity alert if there are tickets and the remaining capacity is less than 20
   */
  const showCapacityAlert = useMemo(() => {
    if (!hasTickets) return false;
    if (!eventDateRemainingCapacity) return false;
    return eventDateRemainingCapacity <= 20;
  }, [eventDateRemainingCapacity, hasTickets]);

  return (
    <Stack gap="lg">
      <Stack>
        <EventDateTimeCard startingAt={eventDate?.starting_at} endingAt={eventDate?.ending_at} />

        {!!eventDate?.description && (
          <Text fz="sm" px="md">
            {eventDate.description}
          </Text>
        )}
      </Stack>

      <Alert.Collapse
        in={showCapacityAlert}
        type="warning"
        icon={<IconTicket />}
        title={`Only ${eventDateRemainingCapacity} spots left!`}
      >
        This event is almost at capacity--don&apos;t miss your chance!
      </Alert.Collapse>

      {!!eventDate?.description && <Divider size="sm" />}

      <TicketsSection
        title={
          <Text fz="lg" fw="600">
            Tickets
          </Text>
        }
        eventDateId={eventDateId}
        causeId={causeId}
        eventId={eventId}
      />
    </Stack>
  );
};

const EventDateModal = ({
  opened,
  eventDateId,
  causeId,
  eventId,
}: {
  opened: boolean;
  eventDateId?: string;
  causeId?: string;
  eventId?: string;
}) => {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(!!opened);

  const { data: eventDate } = useShowEventDate({
    eventId,
    causeId,
    dateId: eventDateId,
    include: 'event_date_tickets,inventory',
  });
  const { data: eventData } = useShowEvent({
    causeId,
    eventId,
  });

  const title = useMemo(() => {
    if (eventData?.event_dates.length === 1) return 'Get Tickets';
    return eventDate?.event_date.title ?? '';
  }, [eventData?.event_dates, eventDate?.event_date.title]);

  return (
    <Modal
      title={title}
      opened={isModalOpen}
      onClose={() => {
        navigate('..');
        setIsModalOpen(false);
      }}
    >
      <EventDateModalContent
        causeId={causeId}
        eventId={eventId}
        eventDateId={eventDateId}
        eventDate={eventDate?.event_date}
      />
    </Modal>
  );
};

export { EventDateModal };
